import { Component, OnInit, OnDestroy, ElementRef, Renderer2, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { VisitorChatbotService } from '../../services/visitor-chatbot.service';

@Component({
  selector: 'app-visitor-chatbot-button',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TranslateModule
  ],
  template: `
    <button mat-fab
            class="visitor-chat-button"
            [class.visible]="!isVisible"
            [class.index-route]="isIndexPage"
            [class.custom-theme]="customBackgroundColor"
            [style.--custom-theme-color]="customBackgroundColor"
            [style.--chat-button-bg]="buttonBackgroundColor"
            [style.--chat-icon-color]="chatIconColor"
            [matTooltip]="'VISITOR_CHATBOT.OPEN_CHAT' | translate"
            (click)="toggleChat()">
      <div class="chat-icon-wrapper">
        <mat-icon class="chat-icon">support_agent</mat-icon>
        <div class="notification-dot" *ngIf="showNotification"></div>
      </div>
    </button>
  `,
  styles: [`
    .visitor-chat-button {
      position: fixed;
      bottom: 120px; /* Increased spacing - more room from WhatsApp button */
      right: 30px; /* Same horizontal alignment as WhatsApp button */
      width: 60px; /* Same size as WhatsApp button */
      height: 60px; /* Same size as WhatsApp button */
    z-index: 1000000; /* Higher than header toolbar (999999) to ensure it appears above navigation */
  /* Use the theme's primary color for the button background to contrast with the accent icon */
  background: var(--chat-button-bg, var(--mat-theme-primary-color, #1976d2)) !important;

  /* Override for custom theme */
  &.custom-theme:not(.index-route) {
    background: var(--custom-theme-color, #e44891) !important;
  }
  color: #ffffff !important;
      transform: scale(0);
      transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 4px 12px color-mix(in srgb, var(--chat-button-bg, #1976d2) 35%, transparent);
      border: none !important;
      outline: none !important;
      border-radius: 50% !important; /* Ensure perfect circle */
      margin: 0 !important; /* Remove any default margins */
      padding: 0 !important; /* Remove any default padding */

      /* Remove Angular Material default styles */
      .mat-mdc-button-base {
        background: transparent !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      /* Override any conflicting Material styles */
      .mat-mdc-fab {
        border-radius: 50% !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      &.visible {
        transform: scale(1);
      }

    &:hover {
        transform: scale(1.05) !important; /* Subtle hover effect */
  box-shadow: 0 6px 20px color-mix(in srgb, var(--chat-button-bg, #1976d2) 40%, transparent) !important;
  background: color-mix(in srgb, var(--chat-button-bg, #1976d2) 85%, black) !important;
      }

      /* Custom theme hover effect */
      &.custom-theme:not(.index-route):hover {
        box-shadow: 0 6px 20px color-mix(in srgb, var(--custom-theme-color, #e44891) 40%, transparent) !important;
        background: color-mix(in srgb, var(--custom-theme-color, #e44891) 85%, black) !important;
      }

      &:active {
        transform: scale(0.95) !important;
      }

      /* Fix icon wrapper alignment */
      .chat-icon-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        margin: 0;
        padding: 0;

        /* Perfect centering for the icon */
        .chat-icon {
          font-size: 28px !important;
          width: 28px !important;
          height: 28px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          line-height: 1 !important;
          margin: 0 !important;
          padding: 0 !important;
          /* Use secondary (accent) theme color for icon for strong contrast over primary background */
          color: var(--chat-icon-color, var(--mat-theme-accent-color, #9c27b0)) !important;
        }

        /* Notification dot positioning */
        .notification-dot {
          position: absolute;
          top: 4px;
          right: 4px;
          width: 16px;
          height: 16px;
          background-color: #FF3333;
          border-radius: 50%;
          border: 2px solid white;
          animation: pulse 2s infinite;
          z-index: 10;
        }
      }
    }

    /* RTL support */
    [dir="rtl"] .visitor-chat-button {
      right: auto;
      left: 30px;

      .chat-icon-wrapper .notification-dot {
        right: auto;
        left: 4px;
      }
    }

    /* Pulse animation for notification dot */
    @keyframes pulse {
      0%, 100% {
        transform: scale(1);
        opacity: 1;
      }
      50% {
        transform: scale(1.2);
        opacity: 0.8;
      }
    }

    /* Mobile responsiveness */
    @media (max-width: 768px) {
      .visitor-chat-button {
        bottom: 110px; /* More spacing above WhatsApp on mobile */
        right: 20px;
        width: 56px;
        height: 56px;

        .chat-icon-wrapper .chat-icon {
          font-size: 26px !important;
          width: 26px !important;
          height: 26px !important;
        }

        .chat-icon-wrapper .notification-dot {
          top: 2px;
          right: 2px;
          width: 14px;
          height: 14px;
        }
      }

      [dir="rtl"] .visitor-chat-button {
        right: auto;
        left: 20px;

        .chat-icon-wrapper .notification-dot {
          right: auto;
          left: 2px;
        }
      }
    }

    /* Extra small screens */
    @media (max-width: 470px) {
      .visitor-chat-button {
        bottom: 105px; /* Proper spacing on small screens */
        right: 20px;
        width: 52px;
        height: 52px;

        .chat-icon-wrapper .chat-icon {
          font-size: 24px !important;
          width: 24px !important;
          height: 24px !important;
        }

        .chat-icon-wrapper .notification-dot {
          top: 2px;
          right: 2px;
          width: 12px;
          height: 12px;
        }
      }

      [dir="rtl"] .visitor-chat-button {
        right: auto;
        left: 15px;
      }
    }

    /* Large screens - even more spacing */
    @media (min-width: 1200px) {
      .visitor-chat-button {
        bottom: 130px; /* Extra spacing on large screens */
        right: 30px;
      }

      [dir="rtl"] .visitor-chat-button {
        right: auto;
        left: 30px;
      }
    }

    /* Prevent any z-index conflicts with other floating elements */
    .visitor-chat-button {
      isolation: isolate;
    }

    /* Adjust position for pages with side navigation (like support page) */
    /* Check if we're inside the main application layout */
    :host-context(.contentWrapper) .visitor-chat-button,
    :host-context(mat-sidenav-content) .visitor-chat-button {
      /* Adjust for side navigation - assuming 280px width when open */
      right: 320px; /* 280px sidenav + 40px extra margin for safety */
    }

    /* For collapsed navigation (62px width) */
    :host-context(.sidenav-collapsed) .visitor-chat-button {
      right: 100px; /* 62px collapsed + 38px margin */
    }

    /* Mobile view - keep original position */
    @media (max-width: 768px) {
      :host-context(.contentWrapper) .visitor-chat-button,
      :host-context(mat-sidenav-content) .visitor-chat-button {
        right: 30px; /* Original mobile position */
      }
    }

    /* RTL support for pages with side navigation */
    [dir="rtl"] :host-context(.contentWrapper) .visitor-chat-button,
    [dir="rtl"] :host-context(mat-sidenav-content) .visitor-chat-button {
    right: auto;
    inset-inline-end: 30px;
    bottom: 60px;
    }

    [dir="ltr"] :host-context(.contentWrapper) .visitor-chat-button,
    [dir="ltr"] :host-context(mat-sidenav-content) .visitor-chat-button {
    left: auto;
    inset-inline-end: 30px;
    bottom: 60px;
    }
    [dir="rtl"] :host-context(.sidenav-collapsed) .visitor-chat-button {
    right: auto;
    inset-inline-end: 30px;
    bottom: 60px;
    }

    /* RTL mobile support for pages with side navigation */
    @media (max-width: 768px) {
      [dir="rtl"] :host-context(.contentWrapper) .visitor-chat-button,
      [dir="rtl"] :host-context(mat-sidenav-content) .visitor-chat-button {
        left: 30px;
        right: auto;
      }
    }

    /* Dynamic positioning class for main app pages */
    [dir="ltr"] .visitor-chat-button.main-app-position {
      right: 0; /* Position at the right edge for English (LTR) */
    }

    /* For mobile, keep original position even in main app */
    @media (max-width: 768px) {
      .visitor-chat-button.main-app-position {
        right: 30px;
      }
    }

    /* RTL support for dynamic positioning */
    :host-context([dir="rtl"]) .visitor-chat-button.main-app-position,
    [dir="rtl"] .visitor-chat-button.main-app-position {
      right: 107em; /* Position at 105em from right edge for Arabic (RTL) */
      left: auto;
    }

    /* RTL mobile support for dynamic positioning */
    @media (max-width: 768px) {
      [dir="rtl"] .visitor-chat-button.main-app-position {
        left: 30px;
        right: auto;
      }
    }
  `]
})
export class VisitorChatbotButtonComponent implements OnInit, OnDestroy {
  @Input() customBackgroundColor?: string;

  isVisible = false;
  showNotification = true;
  isInMainApp = false; // Flag to detect if we're in main app pages

  private subscriptions = new Subscription();
  private currentRoute = '';

  constructor(
    private chatbotService: VisitorChatbotService,
    private router: Router,
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(
      this.chatbotService.isVisible$.subscribe(visible => {
        this.isVisible = visible;
        if (visible) {
          this.showNotification = false; // Hide notification when chat is opened
        }
      })
    );

    // Listen to route changes to detect main app pages
    this.subscriptions.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((event: NavigationEnd) => {
        this.currentRoute = event.urlAfterRedirects || event.url || '';
        this.checkAndAdjustPosition(this.currentRoute);
      })
    );

    // Check initial route
    this.currentRoute = this.router.url || '';
    this.checkAndAdjustPosition(this.currentRoute);

    // Hide notification after 10 seconds
    setTimeout(() => {
      this.showNotification = false;
    }, 10000);
  }

  private checkAndAdjustPosition(url: string): void {
    // Check if we're in main app pages (not auth/index pages)
    const isIndexPage = url === '/' || url.includes('/index') || url.includes('/auth');
    this.isInMainApp = !isIndexPage;

    // Add or remove CSS class to adjust positioning
    const button = this.elementRef.nativeElement.querySelector('.visitor-chat-button');
    if (button) {
      if (this.isInMainApp) {
        this.renderer.addClass(button, 'main-app-position');
      } else {
        this.renderer.removeClass(button, 'main-app-position');
      }
    }
  }

  get isIndexPage(): boolean {
    const normalizedPath = (this.currentRoute || '/')
      .toLowerCase()
      .split('?')[0]
      .split('#')[0]
      .replace(/\/+$/, '') || '/';
    return normalizedPath === '/' || /(^|\/)index(\/|$)/.test(normalizedPath);
  }

  get buttonBackgroundColor(): string {
    if (this.isIndexPage) {
      return '#6a1b9a';
    }
    return this.customBackgroundColor || 'var(--mat-theme-primary-color, #1976d2)';
  }

  get chatIconColor(): string {
    if (this.isIndexPage) {
      return '#e44891';
    }
    return 'var(--mat-theme-accent-color, #9c27b0)';
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  toggleChat(): void {
    const currentRoute = this.router.url;

    // For visitor chat (index/public pages), use 'support' source
    if (currentRoute === '/' || currentRoute.includes('/index') || currentRoute.includes('/auth')) {
      this.chatbotService.openChatWithPageContext(currentRoute, 'support');
    }
    // For support pages, use 'sanad' source and start a new conversation
    else if (currentRoute.includes('/pages/support')) {
      // Start a new conversation to ensure fresh chat when floating button is clicked on support page
      this.chatbotService.startNewConversation();
      this.chatbotService.openChatWithPageContext(currentRoute, 'sanad');
    }
    // For other internal pages, default to 'sanad' source
    else {
      this.chatbotService.openChatWithPageContext(currentRoute, 'sanad');
    }
  }
}
